import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import iconYachts from "@/assets/icon-yachts.webp";
import iconDrive from "@/assets/icon-drive.webp";
import iconResidences from "@/assets/icon-residences.webp";
import iconLifestyle from "@/assets/icon-lifestyle.webp";

const ServicesGrid = () => {
  const { t } = useLanguage();

  const services = [
    {
      name: "Arzani Yachts",
      description: t(
        "Private yacht charters across the Mediterranean's most sought-after destinations.",
        "Privé jachtcharters op de meest gewilde bestemmingen van de Middellandse Zee."
      ),
      icon: iconYachts,
      path: "/yachts",
    },
    {
      name: "Arzani Drive",
      description: t(
        "Prestige vehicles delivered directly to your hotel, villa, or airport. Flexible arrangements, and seamless coordination from arrival to departure.",
        "Prestigieuze voertuigen direct geleverd bij uw hotel, villa of luchthaven. Flexibele arrangementen en naadloze coördinatie van aankomst tot vertrek."
      ),
      icon: iconDrive,
      path: "/drive",
    },
    {
      name: "Arzani Residences",
      description: t(
        "A curated selection of exceptional villas and refined private accommodations. Each residence is secured, prepared, and tailored to your stay.",
        "Een samengestelde selectie van uitzonderlijke villa's en verfijnde privéaccommodaties. Elke residentie is beveiligd, voorbereid en afgestemd op uw verblijf."
      ),
      icon: iconResidences,
      path: "/residences",
    },
    {
      name: "Arzani Lifestyle",
      description: t(
        "Private chauffeur services, VIP transport, and priority reservations at leading restaurants and exclusive clubs. Access arranged in advance, executed effortlessly.",
        "Privé chauffeursdiensten, VIP-vervoer en prioriteitsreserveringen bij toonaangevende restaurants en exclusieve clubs. Toegang vooraf geregeld, moeiteloos uitgevoerd."
      ),
      icon: iconLifestyle,
      path: "/lifestyle",
    },
  ];

  return (
    <section id="services" className="section-spacing bg-secondary/40">
      <div className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-4 tracking-wide uppercase">
              {t("Arzani Services", "Arzani Diensten")}
            </h2>
            <span className="font-accent text-muted-foreground italic block mb-12 text-sm">
              Arzani Group Worldwide
            </span>

            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-16 max-w-md">
              {t(
                "Through Arzani Group, every element of your journey is thoughtfully arranged — from days at sea and luxury vehicles to private villas, chauffeur service, and priority reservations at leading restaurants and clubs. Executed flawlessly, without compromise.",
                "Via Arzani Group wordt elk element van uw reis zorgvuldig geregeld — van dagen op zee en luxe voertuigen tot privévilla's, chauffeursdiensten en prioriteitsreserveringen bij toonaangevende restaurants en clubs. Feilloos uitgevoerd, zonder compromis."
              )}
            </p>

            <p className="font-body text-muted-foreground text-sm leading-[1.9] max-w-md">
              {t(
                "Our team remains personally available at all times, offering preferred rates across our destinations while delivering a truly tailored experience. Beyond service, Arzani is building a refined community — connecting like-minded individuals who value access, discretion, and distinction.",
                "Ons team blijft te allen tijde persoonlijk beschikbaar, met voorkeurstarieven op al onze bestemmingen en een werkelijk op maat gemaakte ervaring. Meer dan service bouwt Arzani een verfijnde gemeenschap — die gelijkgestemde individuen verbindt die toegang, discretie en onderscheiding waarderen."
              )}
            </p>
          </motion.div>

          <motion.div
            className="space-y-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {services.map((service) => (
              <Link key={service.name} to={service.path} className="flex gap-6 items-start group">
                <img
                  src={service.icon}
                  alt={service.name}
                  className="w-14 h-14 object-contain flex-shrink-0 mt-1 brightness-0"
                />
                <div>
                  <h3 className="font-display-medium text-lg md:text-xl text-foreground mb-2 tracking-wide uppercase group-hover:text-accent transition-colors duration-500">
                    {service.name}
                  </h3>
                  <p className="font-body text-muted-foreground text-sm leading-[1.8]">
                    {service.description}
                  </p>
                </div>
              </Link>
            ))}

            <button
              onClick={() => {
                const el = document.getElementById("inquire");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] mt-4"
            >
              {t("Learn more about our services", "Meer over onze diensten")}
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
